import { formatDate, PageHeader, Select, Tabs } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useState, type ReactNode } from 'react';
import { PeriodChips } from '../PeriodChips.tsx';
import { lastDayOf, reportPeriodOf, type PeriodPreset, type ReportPeriod } from '../report-period.ts';
import { useCarriers } from '../settings/carriers.ts';
import { pickName } from '../catalog/categories.ts';
import { useOptions } from '../catalog/useCatalogQueries.ts';
import { usePageContext } from '../usePageContext.ts';
import { ConfirmationStatistics } from './ConfirmationStatistics.tsx';
import { DeliveryStatistics } from './DeliveryStatistics.tsx';
import { MarketingStatistics } from './MarketingStatistics.tsx';
import { OrderStatistics } from './OrderStatistics.tsx';
import { ProductStatistics } from './ProductStatistics.tsx';
import { isStatisticsTab, STATISTICS_TABS, type StatisticsTab } from './statistics.ts';
import styles from './Statistics.module.css';

const ALL_CARRIERS = '';

const BY_VARIANT = '';

export type StatisticsPageProps = {
  api: TypedClient;
  shipping: ShippingClient;
  tab: StatisticsTab;
  preset: PeriodPreset;
  carrier: string | null;
  groupBy: string | null;
  onTab: (tab: StatisticsTab) => void;
  onPreset: (preset: PeriodPreset) => void;
  onCarrier: (carrier: string | null) => void;
  onGroupBy: (groupBy: string | null) => void;
};

type TabProps = {
  api: TypedClient;
  shipping: ShippingClient;
  period: ReportPeriod;
  carrier: string | null;
  groupBy: string | null;
};

const TAB_VIEWS: Readonly<Record<StatisticsTab, (props: TabProps) => ReactNode>> = {
  delivery: DeliveryStatistics,
  confirmation: ConfirmationStatistics,
  products: ProductStatistics,
  orders: OrderStatistics,
  marketing: MarketingStatistics,
};

export const StatisticsPage = ({
  api,
  shipping,
  tab,
  preset,
  carrier,
  groupBy,
  onTab,
  onPreset,
  onCarrier,
  onGroupBy,
}: StatisticsPageProps) => {
  const { t, locale } = usePageContext();
  const [openedAt] = useState(() => new Date());
  const period = reportPeriodOf(preset, openedAt);
  const carriers = useCarriers(shipping).data?.items ?? [];
  const options = useOptions(api).data?.items ?? [];
  const View = TAB_VIEWS[tab];
  const scope = t(`statistics.scope_${tab}`, {
    from: formatDate(period.fromDay, locale, 'long'),
    to: formatDate(lastDayOf(period), locale, 'long'),
  });
  return (
    <>
      <PageHeader title={t('statistics.title')} context={t('statistics.context')} />
      <Tabs
        activeId={tab}
        onChange={(id) => {
          if (isStatisticsTab(id)) onTab(id);
        }}
        tabs={STATISTICS_TABS.map((id) => ({
          id,
          label: t(`statistics.tab_${id}`),
          content:
            id === tab ? (
              <div className={styles['stack']}>
                <div className={styles['filters']}>
                  <PeriodChips value={preset} onChange={onPreset} />
                  {tab === 'delivery' ? (
                    <Select
                      label={t('statistics.carrierFilter')}
                      hideLabel
                      value={carrier ?? ALL_CARRIERS}
                      options={[
                        { value: ALL_CARRIERS, label: t('statistics.allCarriers') },
                        ...carriers.map((entry) => ({ value: entry.code, label: entry.name })),
                      ]}
                      onChange={(event) => {
                        onCarrier(event.target.value === ALL_CARRIERS ? null : event.target.value);
                      }}
                    />
                  ) : null}
                  {tab === 'products' && options.length > 0 ? (
                    <Select
                      label={t('statistics.groupBy')}
                      value={groupBy ?? BY_VARIANT}
                      options={[
                        { value: BY_VARIANT, label: t('statistics.byVariant') },
                        ...options.map((option) => ({
                          value: option.id,
                          label: pickName(option.names, locale, option.code),
                        })),
                      ]}
                      onChange={(event) => {
                        onGroupBy(event.target.value === BY_VARIANT ? null : event.target.value);
                      }}
                    />
                  ) : null}
                </div>
                <p className={styles['scope']}>{scope}</p>
                <View api={api} shipping={shipping} period={period} carrier={carrier} groupBy={groupBy} />
              </div>
            ) : null,
        }))}
      />
    </>
  );
};
